import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api/api";
import {
  Search,
  Columns2,
  ArrowUpDown,
  SlidersHorizontal,
  Table2,
  List,
  MoreHorizontal,
  ChevronDown,
  Info,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ChevronsUpDown,
  Plus,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import type { Recipe, RecipeShelfLife, ShelfLifeUnit } from "@/types/recipe";
import {
  useRecipeShelfLife,
  useCreateShelfLife,
  useUpdateShelfLife,
  useDeleteShelfLife,
} from "@/hooks/recipes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  storageMethodOptions,
  useStorageMethodNames,
  useStorageMethods,
} from "./storage-methods";
import {
  SHELF_LIFE_UNIT_KEYS,
  shelfLifePayload,
  useShelfLifeUnitNames,
} from "./shelf-life-units";

interface ShelfLifeStorageTabProps {
  recipe: Recipe;
  canManage?: boolean;
}

interface RowFormState {
  /** Which stage of the recipe's life this row is about — "Cooked", "Hot hold". */
  stage: string;
  storageMethod: string;
  coolingInstructions: string;
  labelingRequired: boolean;
  usedByDate: string;
  lifeValue: string;
  lifeUnit: ShelfLifeUnit;
}

const EMPTY_FORM: RowFormState = {
  stage: "",
  storageMethod: "",
  coolingInstructions: "",
  labelingRequired: false,
  usedByDate: "",
  lifeValue: "",
  lifeUnit: "DAYS",
};

const ROWS_PER_PAGE = 10;

export function ShelfLifeStorageTab({ recipe, canManage = false }: ShelfLifeStorageTabProps) {
  const { t } = useLingui();
  const storageMethodNames = useStorageMethodNames();
  const storageMethods = useStorageMethods();
  const shelfLifeUnitNames = useShelfLifeUnitNames();
  const recipeId = String(recipe.id);

  const { data, isLoading, isError } = useRecipeShelfLife(recipeId);
  const createMutation = useCreateShelfLife();
  const updateMutation = useUpdateShelfLife();
  const deleteMutation = useDeleteShelfLife();

  const [search, setSearch] = useState("");
  const [view, setView] = useState<"table" | "list">("table");
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<RecipeShelfLife | null>(null);
  const [form, setForm] = useState<RowFormState>(EMPTY_FORM);

  const allRows = data?.data ?? [];
  const filteredRows = search
    ? allRows.filter(
        (r) =>
          r.stage.toLowerCase().includes(search.toLowerCase()) ||
          r.storageMethod?.toLowerCase().includes(search.toLowerCase()) ||
          r.coolingInstructions?.toLowerCase().includes(search.toLowerCase()) ||
          r.usedByDate?.toLowerCase().includes(search.toLowerCase()),
      )
    : allRows;

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const pagedRows = filteredRows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);


  function openCreate() {
    setEditingRow(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(row: RecipeShelfLife) {
    setEditingRow(row);
    setForm({
      stage: row.stage,
      storageMethod: row.storageMethod ?? "",
      coolingInstructions: row.coolingInstructions ?? "",
      labelingRequired: row.labelingRequired,
      usedByDate: row.usedByDate ?? "",
      lifeValue: row.shelfLifeValue == null ? "" : String(row.shelfLifeValue),
      lifeUnit: row.shelfLifeUnit ?? "DAYS",
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    const payload = {
      stage: form.stage,
      storageMethod: form.storageMethod || undefined,
      coolingInstructions: form.coolingInstructions || undefined,
      labelingRequired: form.labelingRequired,
      usedByDate: form.usedByDate || undefined,
      ...shelfLifePayload(form.lifeValue, form.lifeUnit),
    };
    try {
      if (editingRow) {
        await updateMutation.mutateAsync({ recipeId, id: String(editingRow.id), data: payload });
      } else {
        await createMutation.mutateAsync({ recipeId, data: payload });
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to save shelf life`));
      return;
    }
    toast.success(t`Shelf life saved`);
    setDialogOpen(false);
  }

  async function handleDelete(row: RecipeShelfLife) {
    try {
      await deleteMutation.mutateAsync({ recipeId, id: String(row.id) });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to remove shelf life`));
      return;
    }
    toast.success(t`Shelf life removed`);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const translateStorageMethod = (method?: string) =>
    method ? storageMethodNames[method] ?? method : undefined;

  return (
    <div className="flex flex-col gap-3 px-6">
      {/* ── Section heading ─────────────────────────────────── */}
      <div className="flex items-center justify-between pl-2">
        <h2 className="text-2xl font-medium text-(--color-text-primary)">{t`Shelf Life & Storage`}</h2>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        {/* Search */}
        <div className="relative w-[300px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-(--color-text-secondary)" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t`Search...`}
            className="pl-11 h-11 rounded-lg border-(--color-border) shadow-xs"
          />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-11 gap-2 rounded-[10px] font-medium">
            <Columns2 className="size-5" />
            {t`Columns`}
          </Button>
          <Button variant="outline" size="sm" className="h-11 gap-2 rounded-[10px] font-medium">
            <ArrowUpDown className="size-5" />
            {t`Sort`}
          </Button>
          <Button variant="outline" size="sm" className="h-11 gap-2 rounded-[10px] font-medium">
            <SlidersHorizontal className="size-5" />
            {t`Filter`}
          </Button>

          {/* View toggle */}
          <div className="flex h-11 items-center gap-0 rounded-lg bg-(--color-surface-muted) p-1" role="group" aria-label={t`View mode`}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setView("table")}
              aria-label={t`Table view`}
              aria-pressed={view === "table"}
              className={`size-9 [&_svg]:size-5 ${view === "table" ? "bg-(--color-surface-page) shadow-xs hover:bg-(--color-surface-page)" : "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-transparent"}`}
            >
              <Table2 />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setView("list")}
              aria-label={t`List view`}
              aria-pressed={view === "list"}
              className={`size-9 [&_svg]:size-5 ${view === "list" ? "bg-(--color-surface-page) shadow-xs hover:bg-(--color-surface-page)" : "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-transparent"}`}
            >
              <List />
            </Button>
          </div>

          {canManage && (
            <Button size="sm" onClick={openCreate} className="h-11 gap-2 rounded-[10px]">
              <Plus className="size-4" />
              {t`Add section`}
            </Button>
          )}
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────── */}
      <div className="rounded-[10px] border border-(--color-border) overflow-hidden">
        {isLoading ? (
          <div className="space-y-0">
            <div className="h-[52px] bg-(--color-surface-muted)/50 border-b border-(--color-border)" />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-(--color-border) last:border-0">
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-4 w-10" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex h-40 items-center justify-center">
            <p className="text-sm text-(--color-danger-text)">{t`Failed to load storage sections. Please try again.`}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-(--color-surface-muted)/50 hover:bg-(--color-surface-muted)/50 h-[52px]">
                <TableHead className="w-[148px] text-[13px]">
                  <Button variant="ghost" className="h-auto gap-1 p-0 font-medium text-(--color-text-secondary) hover:bg-transparent">
                    {t`Stage`}
                    <ChevronDown className="size-4" />
                  </Button>
                </TableHead>
                <TableHead className="w-[200px] text-[13px] font-medium text-(--color-text-secondary)">
                  {t`Storage Method`}
                </TableHead>
                <TableHead className="text-[13px] font-medium text-(--color-text-secondary)">
                  {t`Cooling Instructions`}
                </TableHead>
                <TableHead className="w-[180px] text-[13px]">
                  <div className="flex items-center gap-2 font-medium text-(--color-text-secondary)">
                    {t`Labeling Required`}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="size-4 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t`Whether this storage stage requires a compliance label`}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TableHead>
                <TableHead className="w-[200px] text-[13px] font-medium text-(--color-text-secondary)">
                  {t`Used-By Rule`}
                </TableHead>
                {canManage && <TableHead className="w-11" />}
              </TableRow>
            </TableHeader>

            <TableBody>
              {pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-40 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-sm text-(--color-text-secondary)">
                        {search ? t`No results match your search.` : t`No storage sections yet.`}
                      </p>
                      {!search && (
                        <Button variant="outline" size="sm" onClick={openCreate} className="gap-1.5">
                          <Plus className="size-4" />
                          {t`Add a storage section`}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow key={row.id} className="h-[68px]">
                    <TableCell className="font-semibold text-[14px]">
                      {row.stage}
                    </TableCell>
                    <TableCell className="text-[14px] font-medium">
                      {translateStorageMethod(row.storageMethod) ?? <span className="text-(--color-text-secondary)">—</span>}
                    </TableCell>
                    <TableCell className="text-[14px] font-medium">
                      {row.coolingInstructions ?? <span className="text-(--color-text-secondary)">—</span>}
                    </TableCell>
                    <TableCell className="text-[14px] font-medium">
                      {row.labelingRequired ? t`Yes` : <span className="text-(--color-text-secondary)">{t`No`}</span>}
                    </TableCell>
                    <TableCell className="text-[14px] font-medium">
                      {/* The number and unit are what the label counts from, so
                          they are what this column shows once a stage has them;
                          the typed rule is the fallback (T-1004). */}
                      {row.shelfLifeValue != null && row.shelfLifeUnit ? (
                        `${row.shelfLifeValue} ${shelfLifeUnitNames[row.shelfLifeUnit]}`
                      ) : (
                        row.usedByDate ?? <span className="text-(--color-text-secondary)">—</span>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-11 rounded-[10px]"
                            >
                              <MoreHorizontal className="size-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(row)} className="gap-2">
                              <Pencil className="size-4" />
                              {t`Edit`}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(row)}
                              className="gap-2 text-(--color-danger-text) focus:text-(--color-danger-text)"
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="size-4" />
                              {t`Delete`}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        {/* Table footer */}
        <div className="flex items-center justify-between px-3 py-3 border-t border-(--color-border)">
          <p className="text-sm text-(--color-text-secondary)">
            {t`0 of ${filteredRows.length} row(s) selected.`}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-(--color-text-secondary)">{t`Rows per page`}</span>
              <div className="flex h-11 w-24 items-center justify-between rounded-lg border border-(--color-border) bg-(--color-surface-page) px-4 py-3 shadow-xs text-[16px]">
                <span>{ROWS_PER_PAGE}</span>
                <ChevronsUpDown className="size-4 text-(--color-text-secondary)" />
              </div>
            </div>
            <span className="text-sm font-medium">
              {t`Page ${page} of ${totalPages}`}
            </span>
            <div className="flex items-center gap-2">
              {[
                { icon: ChevronsLeft, action: () => setPage(1), disabled: page === 1 },
                { icon: ChevronLeft, action: () => setPage((p) => Math.max(1, p - 1)), disabled: page === 1 },
                { icon: ChevronRight, action: () => setPage((p) => Math.min(totalPages, p + 1)), disabled: page === totalPages },
                { icon: ChevronsRight, action: () => setPage(totalPages), disabled: page === totalPages },
              ].map(({ icon: Icon, action, disabled }, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="icon"
                  onClick={action}
                  disabled={disabled}
                  className="size-11 rounded-lg shadow-xs"
                >
                  <Icon className="size-5" />
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Add / Edit dialog ────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingRow ? t`Edit Shelf Life` : t`Set Shelf Life`}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            {/* What stage, how long it keeps, how it is stored. */}
            <div className="flex gap-3">
              <div className="flex flex-col gap-1.5 flex-1">
                <Label htmlFor="shelf-life-stage">
                  {t`Stage`} <span className="text-(--color-danger-text)">*</span>
                </Label>
                <Input
                  id="shelf-life-stage"
                  type="text"
                  maxLength={100}
                  value={form.stage}
                  onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))}
                  placeholder={t`e.g. Cooked`}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="shelf-life-value">{t`Shelf life`}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="shelf-life-value"
                    type="number"
                    min={1}
                    max={999}
                    className="w-20"
                    value={form.lifeValue}
                    onChange={(e) => setForm((f) => ({ ...f, lifeValue: e.target.value }))}
                    placeholder={t`e.g. 2`}
                  />
                  {/* Days would not cover a two-hour hot hold (T-1004). */}
                  <Select
                    value={form.lifeUnit}
                    onValueChange={(value) =>
                      setForm((f) => ({ ...f, lifeUnit: value as ShelfLifeUnit }))
                    }
                  >
                    <SelectTrigger aria-label={t`Shelf life unit`} className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SHELF_LIFE_UNIT_KEYS.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {shelfLifeUnitNames[unit]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t`Storage method`}</Label>
              <Select
                value={form.storageMethod}
                onValueChange={(value) => setForm((f) => ({ ...f, storageMethod: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t`Select method`} />
                </SelectTrigger>
                <SelectContent>
                  {storageMethodOptions(editingRow?.storageMethod, storageMethods).map((method) => (
                    <SelectItem key={method} value={method}>
                      {translateStorageMethod(method)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t`Cooling instructions`}</Label>
              <Textarea
                value={form.coolingInstructions}
                onChange={(e) => setForm((f) => ({ ...f, coolingInstructions: e.target.value }))}
                placeholder={t`e.g. Cook from 60°C → 21°C within 2hrs, then to 5°C within 4 hrs`}
                className="resize-none"
                rows={4}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t`Used-By Rule`}</Label>
              <Input
                value={form.usedByDate}
                onChange={(e) => setForm((f) => ({ ...f, usedByDate: e.target.value }))}
                placeholder={t`e.g. Prep date +3 days, End of service`}
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="labeling-required"
                checked={form.labelingRequired}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, labelingRequired: checked }))
                }
              />
              <Label htmlFor="labeling-required">{t`Labeling Required`}</Label>
            </div>

            {form.labelingRequired && (
              <p className="rounded-lg border border-(--color-border) bg-(--color-surface-muted)/40 p-3 text-sm text-(--color-text-secondary)">
                {t`What the label prints is set on the Labeling tab, and which fields it shows is set once for the whole organization in Settings.`}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t`Cancel`}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.stage.trim() || isSaving}
              className="gap-1.5"
            >
              {isSaving && <Loader2 className="size-3.5 animate-spin" />}
              {editingRow ? t`Save Changes` : t`Save`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
